import { createFileRoute, redirect } from "@tanstack/react-router";

import { createHead } from "@/components/createHead";
import { DocsPortableText } from "@/components/DocsPortableText";
import MarketingFooter from "@/components/MarketingFooter";
import { MarketingNavBar } from "@/components/MarketingNavBar";
import { NewsletterPost } from "@/components/NewsletterPost";
import { Page } from "@/components/Page";
import { getPosts } from "@/lib/content";

export const Route = createFileRoute("/blog/$slug")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const posts = await getPosts();
    const post = posts.find((post) => post.slug.current === params.slug);

    if (!post) {
      throw redirect({ to: "/blog" });
    }

    return {
      post,
    };
  },
  head: ({ loaderData }) =>
    createHead({
      title: `${loaderData!.post.title} | Graze`,
      ogTitle: loaderData!.post.title,
      description: loaderData!.post.description,
      pathname: `/blog/${loaderData!.post.slug.current}`,
      ogImage: `/blog/$slug/og.png`,
      ogParams: { slug: loaderData!.post.slug.current },
    }),
});

function RouteComponent() {
  const { post } = Route.useLoaderData();

  return (
    <div className="min-h-screen flex flex-col text-slate-normal instrument-sans relative z-0 bg-slate-app">
      <header className="h-[25vh] md:h-[500px] relative overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="object-cover h-full w-full relative"
        />
        <div className="absolute inset-0 bg-linear-to-b from-10% to-95% from-transparent to-slate-1 dark:to-slatedark-1"></div>
        <div className="w-screen md:max-w-304 mx-auto px-4 md:px-10 xl:px-16 py-4 flex flex-col gap-6 md:gap-8 absolute inset-0 z-1">
          <MarketingNavBar variant="light" showSearch />
        </div>
      </header>
      <Page asChild className="md:max-w-4xl mx-auto -mt-40 z-1 relative">
        <main>
        <div className="flex flex-col gap-6 @container">
          <h1 className="scroll-m-20 font-extrabold tracking-tight instrument-serif text-[max(2.4rem,8cqw)]">
            {post.title}
          </h1>

          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-2 items-center flex-1">
              <span className="font-semibold md:text-lg">
                ~ {post.author.name}
              </span>
              <a
                href={`https://bsky.app/profile/${post.author.handle}`}
                className="text-sm md:text-base text-slate-dim"
              >
                @{post.author.handle}
              </a>
            </div>
            <div className="text-sm md:text-base text-slate-dim font-light">
              {Intl.DateTimeFormat("en-us", {
                month: "long",
                day: "numeric",
                year: "numeric",
              }).format(post.publishedAt)}
            </div>
          </div>
        </div>

        <div>
          {typeof post.body === "string" ? (
            <NewsletterPost post={post.body} isNew />
          ) : (
            <DocsPortableText body={post.body as unknown} isNew />
          )}
        </div>
        </main>
      </Page>
      <MarketingFooter />
    </div>
  );
}
